import CurrencyJS from 'currency.js'
import { GBP } from 'entities/currencies'

/* IMPORTANT!
 *
 * This library casts everything to `number` in order to facilitate easier arithmetic. `number` and `bigint` are both
 * 64-bit int types so theoretically there should never be any truncation errors...?
 * We don't really want to use `bigint` but Prisma forces us to thanks to this bug:
 * https://github.com/prisma/prisma/issues/7570
 */

export const MAX_SAFE_VALUE = Number.MAX_SAFE_INTEGER

type Numeric = number | bigint
type Currency = string
type FormatNotation = 'compact' | 'full'
type FormatOptions = {
    notation?: FormatNotation
    minimumFractionDigits?: number
    maximumFractionDigits?: number
}

const NOTATION_TO_LOCALE_NOTATION = {
    compact: 'compact',
    full: 'standard',
} as const

namespace MoneyInternal {
    const createCurrency = (amount: number) => {
        if (amount > MAX_SAFE_VALUE) throw new Error("We don't support numbers this big")
        return CurrencyJS(amount, { fromCents: true })
    }

    export const add = (...args: number[]): number => {
        const aC = createCurrency(args[0])
        return args.slice(1).reduce((acc, x) => {
            return acc.add(x)
        }, aC).intValue
    }

    export const subtract = (...args: number[]): number => {
        const aC = createCurrency(args[0])
        return args.slice(1).reduce((acc, x) => {
            return acc.subtract(x)
        }, aC).intValue
    }

    export const multiply = (aMoney: number, bScalar: number): number => {
        const aC = createCurrency(aMoney)
        return aC.multiply(bScalar).intValue
    }

    export const divide = (aMoney: number, bScalar: number): number => {
        const aC = createCurrency(aMoney)
        return aC.divide(bScalar).intValue
    }

    export const distribute = (
        aMoney: number,
        nScalar: number,
        opts: { equalDistribution?: boolean } = {},
    ): number[] => {
        const aC = createCurrency(aMoney)
        const distribution = aC.distribute(nScalar).map(x => x.intValue)
        // this is not weird, consult docs https://currency.js.org/#distribute
        if (opts.equalDistribution) {
            const length = distribution.length
            return Array(length).fill(distribution[0])
        } else {
            return distribution
        }
    }

    // use this function if you receive a number from the client that doesn't store the currency
    // in the smallest unit (e.g. cents)
    export const normalize = (aMoney: number): number => {
        return CurrencyJS(aMoney).intValue
    }

    export const denormalize = (aMoney: number): number => {
        return CurrencyJS(aMoney, { fromCents: true }).value
    }

    const formatWithCurrency = (
        amount: number,
        currency: Currency = GBP,
        opt?: FormatOptions,
    ) => {
        const notation = opt?.notation ?? 'full'
        return denormalize(amount)
            .toLocaleString('en-GB', {
                style: 'currency',
                currency,
                minimumFractionDigits: opt?.minimumFractionDigits ?? 2,
                maximumFractionDigits: opt?.maximumFractionDigits ?? 2,
                notation: NOTATION_TO_LOCALE_NOTATION[notation],
            })
            .replace(/\.0+$/, '')
    }
    const formatWithoutCurrency = (
        amount: number,
        currency: Currency = GBP,
        opt?: FormatOptions,
    ) => {
        const notation = opt?.notation ?? 'full'
        return denormalize(amount)
            .toLocaleString('en-GB', {
                currency,
                minimumFractionDigits: opt?.minimumFractionDigits ?? 2,
                maximumFractionDigits: opt?.maximumFractionDigits ?? 2,
                notation: NOTATION_TO_LOCALE_NOTATION[notation],
            })
            .replace(/\.0+$/, '')
    }

    export const format = (
        amount: number,
        opts?:
            | ({
                  withCurrency?: boolean
                  currency?: Currency
              } & FormatOptions)
            | Currency,
    ) => {
        if (typeof opts === 'string') {
            return formatWithCurrency(amount, opts)
        } else {
            const { withCurrency = true, currency = GBP } = opts ?? {}
            return withCurrency
                ? formatWithCurrency(amount, currency, {
                      notation: opts?.notation,
                      minimumFractionDigits: opts?.minimumFractionDigits,
                      maximumFractionDigits: opts?.minimumFractionDigits,
                  })
                : formatWithoutCurrency(amount, currency, {
                      notation: opts?.notation,
                      minimumFractionDigits: opts?.minimumFractionDigits,
                      maximumFractionDigits: opts?.minimumFractionDigits,
                  })
        }
    }

    export const percentageOf = (part: number, whole: number): number => {
        if (whole === 0) {
            console.error('Division by zero is not allowed')
            return 0
        }

        return (part / whole) * 100
    }

    export const percentageAmount = (percentage: number, amount: number) => {
        return amount * (percentage / 100)
    }

    export const roundUpToNearest = (amount: number, nearest: number): number => {
        return Math.ceil(amount / nearest) * nearest
    }

    // function to convert formattedValue e.g $1,000 to 1000_00
    export const convertFormattedValueToNumber = (formattedValue: string): number => {
        return CurrencyJS(formattedValue).intValue
    }
}

export namespace Money {
    const convert = <T extends Numeric>(inferTypeFrom: T, value: Numeric): T => {
        let result: T
        if (typeof inferTypeFrom === 'number') {
            result = Number(value) as T
        } else if (typeof inferTypeFrom === 'bigint') {
            result = BigInt(value) as T
        } else {
            throw new Error('Invalid type')
        }

        if (result > MAX_SAFE_VALUE) throw new Error("We don't support numbers this big")

        return result as T
    }

    //

    export const add = <T extends Numeric>(...args: T[]): T => {
        return convert<T>(args[0], MoneyInternal.add(...args.map(x => Number(x))))
    }

    export const subtract = <T extends Numeric>(...args: T[]): T => {
        return convert<T>(args[0], MoneyInternal.subtract(...args.map(x => Number(x))))
    }

    export const multiply = <T extends Numeric>(aMoney: T, bScalar: number): T => {
        return convert<T>(aMoney, MoneyInternal.multiply(Number(aMoney), bScalar))
    }

    export const divide = <T extends Numeric>(aMoney: T, bScalar: number): T => {
        return convert<T>(aMoney, MoneyInternal.divide(Number(aMoney), bScalar))
    }

    export const distribute = <T extends Numeric>(
        aMoney: T,
        nScalar: number,
        opts: { equalDistribution?: boolean } = {},
    ): T[] => {
        return MoneyInternal.distribute(Number(aMoney), nScalar, opts).map(x =>
            convert<T>(aMoney, x),
        )
    }

    export const normalize = <T extends Numeric>(aMoney: T): number => {
        return MoneyInternal.normalize(Number(aMoney))
    }
    export const denormalize = <T extends Numeric>(aMoney: T): number => {
        return MoneyInternal.denormalize(Number(aMoney))
    }

    export const format = <T extends Numeric>(
        amount: T,
        opts?:
            | ({
                  withCurrency?: boolean
                  currency?: Currency
              } & FormatOptions)
            | Currency,
    ): string => {
        return MoneyInternal.format(Number(amount), opts)
    }

    export const percentageOf = (part: Numeric, whole: Numeric): number => {
        return MoneyInternal.percentageOf(Number(part), Number(whole))
    }

    export const percentageAmount = <T extends Numeric>(
        percentage: number,
        amount: T,
    ): T => {
        return convert<T>(
            amount,
            MoneyInternal.percentageAmount(percentage, Number(amount)),
        )
    }

    export const roundUpToNearest = <T extends Numeric>(
        amount: T,
        nearest: number,
    ): T => {
        return convert<T>(amount, MoneyInternal.roundUpToNearest(Number(amount), nearest))
    }

    export const convertFormattedValueToNumber =
        MoneyInternal.convertFormattedValueToNumber

    export const min = <T extends Numeric>(...args: T[]): T => {
        return convert<T>(args[0], Math.min(...args.map(x => Number(x))))
    }

    export const max = <T extends Numeric>(...args: T[]): T => {
        return convert<T>(args[0], Math.max(...args.map(x => Number(x))))
    }
}
